// @ts-nocheck

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { useQuery } from '@shared/hooks';
import { ThemeContext } from '@shared/store/context/ThemeContext';
import { APP_THEMES } from '@shared/config/constants/app';
import { useEmptySessionIdRedirect } from '@features/thank-you/model/hooks/useEmptySessionIdRedirect';
import { useGetThankYou } from '@features/thank-you/model/hooks/useGetThankYou';
import { useCleanReferrerTillOTP } from '@shared/hooks/useOTPPageIsUsed';
import { useApplyLangAndActivationURL } from '@features/thank-you/model/hooks/useApplyLangAndActivationURL';
import ThankyouV3 from '@features/thank-you/ui/components/ThankyouV3';
import ThankYou from '@features/thank-you/ui/components/Thankyou';
import { isVariationV3Route, isVariationV5Route, setTheme } from '@shared/utils/general';
import { isPT } from '@entities/country';
import { ThankyouGoVip } from '@features/thank-you/ui/components/ThankyouGoVip';
import { ThankyouLgAg26 } from '@features/thank-you/ui/components/ThankyouLgAg26';
import { isAg26MarketingVariation } from '@shared/ui/unlock-vuz-banner';
import { AR_lang, EN_lang } from '@entities/language';
import { useApplyGtagUser } from '@shared/hooks/useApplyGtagUser';
import { dcbAtom } from '@shared/store/slice-atoms';

const KNOWN_LANG_CODES = ['en', 'ar', 'es', 'fr', 'id', 'ms', 'tr', 'no', 'kr', 'pt'];

const isKnownLangCode = (value) =>
  Boolean(value && KNOWN_LANG_CODES.includes(String(value).toLowerCase()));

const ThankYouParent = () => {
  const [hideDownload, setHideDownload] = useState(true);
  const [activationURL, setActivationURL] = useState(null);
  const routeParams = useParams();
  const { query } = useQuery();

  // NOTE: /thankyou/:sessionId can be a real sessionId OR a lang code (/thankyou/en)
  const singleSegmentParam = !routeParams.lang ? routeParams.sessionId : null;
  const singleSegmentIsLang = isKnownLangCode(singleSegmentParam);

  const lang =
    routeParams.lang ||
    (singleSegmentIsLang ? singleSegmentParam : null) ||
    query.get('lang') ||
    query.get('language');

  const sessionId = singleSegmentIsLang
    ? query.get('sessionId')
    : routeParams.sessionId || query.get('sessionId');

  const aggregator = query.get('aggregator');
  const showOpenButton = query.get('showOpenButton') === 'true';

  const dcb = useAtomValue(dcbAtom);
  const { thankyouData, operator, otpData, propsInState, homeData } = dcb;
  const { languageConfig: languageConfigFromState } = propsInState || {};
  const languageConfig = languageConfigFromState?.availableLanguages
    ? languageConfigFromState
    : isAg26MarketingVariation()
    ? { availableLanguages: [EN_lang, AR_lang] }
    : languageConfigFromState || {};

  useApplyGtagUser(otpData?.subscriberId || null);

  const isV3Route = isVariationV3Route(dcb.variationKey);
  const isV5Route = isVariationV5Route(dcb.variationKey);
  const isLgAg26 = isAg26MarketingVariation();

  const noRedirect =
    (query.get('noRedirect') && query.get('noRedirect') === 'true') || isV5Route || isLgAg26;

  // NOTE: Lang-only URLs (/thankyou/en) intentionally omit sessionId
  const allowEmptySessionId = Boolean(lang) && !sessionId;

  const isPortugalThankYou =
    String(lang || '').toLowerCase() === 'pt' ||
    isPT(thankyouData?.country) ||
    isPT(homeData?.country);

  const theme = isV3Route
    ? APP_THEMES.BLUE
    : isV5Route || isLgAg26 || isPortugalThankYou
    ? APP_THEMES.WHITE
    : APP_THEMES.BLACK;

  useEffect(() => {
    if (isPortugalThankYou) {
      setTheme(APP_THEMES.WHITE);
    }
  }, [isPortugalThankYou]);

  useCleanReferrerTillOTP();

  useEmptySessionIdRedirect(noRedirect, sessionId, allowEmptySessionId);

  const shouldFetchThankYou = Boolean(sessionId) || allowEmptySessionId || noRedirect;

  useGetThankYou(sessionId, operator.isShownMsisdn, aggregator, shouldFetchThankYou);

  useApplyLangAndActivationURL(lang, thankyouData, noRedirect, setActivationURL, setHideDownload);

  const thankYouPage = useMemo(() => {
    if (isLgAg26) {
      return (
        <ThankyouLgAg26
          activationURL={activationURL}
          languageConfig={languageConfig}
          showOpenButton={showOpenButton}
        />
      );
    }

    if (isV5Route) {
      return (
        <ThankyouGoVip
          activationURL={activationURL}
          languageConfig={languageConfig}
          showOpenButton={showOpenButton}
        />
      );
    }

    return isV3Route ? (
      <ThankyouV3
        languageConfig={languageConfig}
        activationURL={activationURL}
        showOpenButton={showOpenButton}
      />
    ) : (
      <ThankYou
        activationURL={activationURL}
        hideDownload={hideDownload}
        languageConfig={languageConfig}
        showOpenButton={showOpenButton}
      />
    );
  }, [
    isV3Route,
    isV5Route,
    isLgAg26,
    activationURL,
    hideDownload,
    languageConfig,
    showOpenButton,
  ]);

  return <ThemeContext.Provider value={{ theme }}>{thankYouPage}</ThemeContext.Provider>;
};

export { ThankYouParent };
